import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Button,
    Chip,
    Description,
    Label,
    Modal,
    NumberField,
    Surface,
    Typography,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { apiErrorMessage, formatGel, getNodeTestIdProp } from '@/utils';
import {
    CASH_COUNT_FORMAT,
    discrepancyTone,
    formatShiftTime,
} from '../../shiftsMeta';
import type { ShiftCloseLogic } from './useShiftClose';

type ShiftCloseModalProps = ShiftCloseLogic;

const Figure: React.FC<{ label: string; value: number; testId: string }> = ({
    label,
    value,
    testId,
}) => (
    <div className="flex items-center justify-between">
        <Typography color="muted" type="body-sm">
            {label}
        </Typography>
        <Typography
            className="tabular-nums"
            type="body-sm"
            {...getNodeTestIdProp(testId)}
        >
            {formatGel(value)}
        </Typography>
    </div>
);

/**
 * The admin's close of a stuck shift (render-only): whose shift it is, a blind
 * count, and a refusal in the admin's language; then the expected cash, the
 * count and the difference as the server recorded them.
 */
export const ShiftCloseModal: React.FC<ShiftCloseModalProps> = ({
    target,
    step,
    counted,
    setCounted,
    canConfirm,
    isSubmitting,
    error,
    record,
    onConfirm,
    onDismiss,
}) => {
    const { t } = useTranslation('pages');
    const tone = record ? discrepancyTone(record.difference) : null;

    return (
        <Modal.Backdrop
            isDismissable={!isSubmitting}
            isOpen={target !== null}
            onOpenChange={(open) => {
                if (!open) {
                    onDismiss();
                }
            }}
            variant="blur"
        >
            <Modal.Container placement="center" size="md">
                <Modal.Dialog {...getNodeTestIdProp('shift-close')}>
                    <Modal.CloseTrigger />
                    <Modal.Header>
                        <Modal.Icon>
                            <Icon
                                className="size-5"
                                icon="solar:lock-keyhole-bold-duotone"
                            />
                        </Modal.Icon>
                        <Modal.Heading>{t('shifts.close.title')}</Modal.Heading>
                    </Modal.Header>
                    <Modal.Body>
                        {target ? (
                            <div className="flex flex-col gap-4">
                                <div className="flex flex-col">
                                    <Typography type="body" weight="semibold">
                                        {target.sellerName ??
                                            t('shifts.unknownSeller')}
                                    </Typography>
                                    <Typography color="muted" type="body-xs">
                                        {t('shifts.close.openedAt', {
                                            time: formatShiftTime(
                                                target.openedAt,
                                            ),
                                        })}
                                    </Typography>
                                </div>

                                {step === 'count' ? (
                                    <div className="flex flex-col gap-2">
                                        <NumberField
                                            formatOptions={CASH_COUNT_FORMAT}
                                            fullWidth
                                            isDisabled={isSubmitting}
                                            minValue={0}
                                            onChange={(value) =>
                                                setCounted(
                                                    value === undefined ||
                                                        Number.isNaN(value)
                                                        ? null
                                                        : value,
                                                )
                                            }
                                            value={counted ?? undefined}
                                            variant="secondary"
                                            {...getNodeTestIdProp(
                                                'shift-close-count',
                                            )}
                                        >
                                            <Label>
                                                {t('shifts.close.countLabel')}
                                            </Label>
                                            <NumberField.Group>
                                                <NumberField.DecrementButton />
                                                <NumberField.Input className="w-full tabular-nums" />
                                                <NumberField.IncrementButton />
                                            </NumberField.Group>
                                            <Description>
                                                {t('shifts.close.countHint')}
                                            </Description>
                                        </NumberField>
                                        {error ? (
                                            <Alert
                                                {...getNodeTestIdProp(
                                                    'shift-close-error',
                                                )}
                                                role="alert"
                                                status="danger"
                                            >
                                                <Alert.Indicator />
                                                <Alert.Content>
                                                    <Alert.Title>
                                                        {apiErrorMessage(
                                                            error,
                                                            t,
                                                        )}
                                                    </Alert.Title>
                                                </Alert.Content>
                                            </Alert>
                                        ) : null}
                                    </div>
                                ) : null}

                                {step === 'closed' && record ? (
                                    <div className="flex flex-col gap-3">
                                        <Typography
                                            color="muted"
                                            type="body-sm"
                                        >
                                            {t('shifts.close.closedSummary')}
                                        </Typography>
                                        <Surface
                                            className="flex flex-col gap-2 p-3"
                                            variant="secondary"
                                            {...getNodeTestIdProp(
                                                'shift-close-record',
                                            )}
                                        >
                                            <Figure
                                                label={t(
                                                    'shifts.summary.expected',
                                                )}
                                                testId="shift-close-record-expected"
                                                value={record.expected}
                                            />
                                            <Figure
                                                label={t(
                                                    'shifts.summary.counted',
                                                )}
                                                testId="shift-close-record-counted"
                                                value={record.counted}
                                            />
                                            <div className="flex items-center justify-between">
                                                <Typography
                                                    color="muted"
                                                    type="body-sm"
                                                >
                                                    {t(
                                                        'shifts.summary.difference',
                                                    )}
                                                </Typography>
                                                <Chip
                                                    color={tone ?? 'default'}
                                                    size="sm"
                                                    variant="soft"
                                                    {...getNodeTestIdProp(
                                                        'shift-close-record-difference',
                                                    )}
                                                >
                                                    {formatGel(
                                                        record.difference,
                                                    )}
                                                </Chip>
                                            </div>
                                        </Surface>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </Modal.Body>
                    <Modal.Footer>
                        {step === 'count' ? (
                            <>
                                <Button
                                    {...getNodeTestIdProp('shift-close-cancel')}
                                    isDisabled={isSubmitting}
                                    onPress={onDismiss}
                                    variant="outline"
                                >
                                    {t('shifts.close.cancel')}
                                </Button>
                                <Button
                                    {...getNodeTestIdProp(
                                        'shift-close-confirm',
                                    )}
                                    isDisabled={!canConfirm}
                                    isPending={isSubmitting}
                                    onPress={onConfirm}
                                    variant="danger"
                                >
                                    {t('shifts.close.confirm')}
                                </Button>
                            </>
                        ) : (
                            <Button
                                {...getNodeTestIdProp('shift-close-done')}
                                onPress={onDismiss}
                            >
                                {t('shifts.close.done')}
                            </Button>
                        )}
                    </Modal.Footer>
                </Modal.Dialog>
            </Modal.Container>
        </Modal.Backdrop>
    );
};
